// src/pages/Login.js

import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate, useLocation } from "react-router-dom";
import styles from "./Login.module.css";
import { GoogleLogin } from "@react-oauth/google";
import API_BASE_URL from "../config";
import { trackEvent } from "../utils/analytics";
import { motion, AnimatePresence } from "framer-motion";

function Login({ onLoginSuccess }) {
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    trackEvent("login_view");
  }, []);

  async function requestOtp(e) {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      trackEvent("otp_requested", { source: "login", method: "email" });
      await axios.post(`${API_BASE_URL}/api/auth/request-otp`, { email });
      setStep(2);
    } catch (err) {
      trackEvent("otp_request_failed", {
        source: "login",
        method: "email",
        reason: err.response?.data?.error || "request_failed"
      });
      setError(err.response?.data?.error || "Failed to send OTP");
    } finally {
      setLoading(false);
    }
  }

  async function verifyOtp(e) {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/auth/verify-otp`,
        { email, otp }
      );
      trackEvent("login_completed", { method: "otp" });

      localStorage.setItem("token", response.data.access_token);
      localStorage.setItem("user", JSON.stringify(response.data.user));

      if (onLoginSuccess) onLoginSuccess(response.data.user);

      const redirectPath = location.state?.from || "/";
      navigate(redirectPath, { replace: true });
    } catch (err) {
      trackEvent("login_failed", {
        method: "otp",
        reason: err.response?.data?.error || "invalid_otp"
      });
      setError(err.response?.data?.error || "Invalid OTP");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className={styles.loginOverlay}>
      <motion.div
        className={styles.loginModal}
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ duration: 0.3, ease: "easeOut" }}
      >
        <div className={styles.loginHeader}>
          <div>
            <p className={styles.eyebrow}>ZenQB Assistant</p>
            <h2 className={styles.title}>
              {step === 1 ? "Welcome back!" : "Enter your code"}
            </h2>
          </div>
          <button
            className={styles.closeButton}
            onClick={() => navigate("/")}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <div className={styles.loginSubheader}>
          <span className={styles.stepBadge}>
            Step {step} of {step === 1 ? "Email" : "Verify"}
          </span>
        </div>

        <div className={styles.loginBody}>
          <AnimatePresence mode="wait">
            {step === 1 ? (
              <motion.div
                key="step1"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                transition={{ duration: 0.2 }}
              >
                <p className={styles.subtitle}>
                  Enter your email to continue with OTP authentication
                </p>

                <form onSubmit={requestOtp}>
                  <div className={styles.inputGroup}>
                    <input
                      type="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="your.email@example.com"
                      className={styles.input}
                    />
                  </div>

                  {error && <p className={styles.error}>{error}</p>}

                  <button
                    type="submit"
                    className={styles.submitButton}
                    disabled={loading}
                  >
                    {loading ? "Sending..." : "Continue"}
                  </button>
                </form>

                <div className={styles.divider}>
                  <span>OR</span>
                </div>

                <GoogleLogin
                  onSuccess={async (credentialResponse) => {
                    try {
                      const response = await axios.post(
                        `${API_BASE_URL}/api/auth/google`,
                        { token: credentialResponse.credential }
                      );
                      trackEvent("login_completed", { method: "google" });

                      localStorage.setItem("token", response.data.access_token);
                      localStorage.setItem("user", JSON.stringify(response.data.user));

                      if (onLoginSuccess) onLoginSuccess(response.data.user);
                      navigate("/", { replace: true });
                    } catch (err) {
                      trackEvent("login_failed", { method: "google", reason: err?.message || "request_failed" });
                      setError("Google login failed");
                    }
                  }}
                  onError={() => {
                    trackEvent("login_failed", { method: "google", reason: "google_sdk_error" });
                    setError("Google login failed");
                  }}
                />
              </motion.div>
            ) : (
              <motion.div
                key="step2"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                transition={{ duration: 0.2 }}
              >
                <p className={styles.subtitle}>
                  We've sent a 6-digit code to <strong>{email}</strong>
                </p>

                <form onSubmit={verifyOtp}>
                  <div className={styles.inputGroup}>
                    <input
                      type="text"
                      required
                      value={otp}
                      onChange={(e) => setOtp(e.target.value)}
                      placeholder="123456"
                      className={styles.input}
                      maxLength={6}
                    />
                  </div>

                  {error && <p className={styles.error}>{error}</p>}

                  <button
                    type="submit"
                    className={styles.submitButton}
                    disabled={loading}
                  >
                    {loading ? "Verifying..." : "Verify & Login"}
                  </button>

                  <button
                    type="button"
                    className={styles.backButton}
                    onClick={() => {
                      setStep(1);
                      setOtp("");
                      setError("");
                    }}
                  >
                    ← Back to email
                  </button>
                </form>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </motion.div>
    </div>
  );
}

export default Login;